import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../config/firebase';
import {
  Message,
  CreateMessageParams,
  MessageSearchParams,
  MessageValidationError,
} from '../types/message';
import { ConversationService } from './conversationService';
import { ChangeTrackingService } from './changeTrackingService';
import { NotificationService } from './notificationService';
import { mentionService } from './mentionService';

/**
 * MessageService - Manages messages within conversations
 *
 * Features:
 * - Send text, image, file, and genealogy link messages
 * - Edit messages (within 24 hours)
 * - Delete messages (soft delete)
 * - Reactions (emoji support)
 * - Read receipts
 * - Reply threading
 * - Message search
 * - Pagination support
 *
 * Integration:
 * - Updates conversation lastMessage and lastActivity
 * - Increments unread counts for participants
 * - Sends notifications based on user preferences
 * - Logs all changes via ChangeTrackingService
 */
class MessageServiceImpl {
  private readonly messagesCollection = 'messages';

  /**
   * Send a new message
   */
  async sendMessage(
    params: CreateMessageParams,
    senderId: string
  ): Promise<string> {
    try {
      // 1. Validate params
      this.validateCreateMessage(params);

      // 2. Check if user is participant in conversation
      const conversation = await ConversationService.getConversationById(
        params.conversationId
      );
      if (!conversation) {
        throw new MessageValidationError('Conversation not found');
      }
      if (!conversation.participants.includes(senderId)) {
        throw new MessageValidationError('User is not a participant in this conversation');
      }

      // 3. Create message document
      const messageId = doc(collection(db, this.messagesCollection)).id;
      const now = new Date();

      const message: Message = {
        id: messageId,
        conversationId: params.conversationId,
        senderId,
        type: params.type,
        content: params.content,
        ...(params.attachments && params.attachments.length > 0 && {
          attachments: params.attachments.map(att => ({
            ...att,
            id: uuidv4(),
          })),
        }),
        ...(params.replyTo && { replyTo: params.replyTo }),
        ...(params.mentions && params.mentions.length > 0 && { mentions: params.mentions }),
        reactions: {},
        readBy: [senderId], // Sender has read their own message
        edited: false,
        deleted: false,
        createdAt: now,
      };

      // 4. Save to Firestore
      await setDoc(
        doc(db, this.messagesCollection, messageId),
        this.toFirestore(message)
      );

      // 5. Update conversation lastMessage and lastActivity
      await this.updateConversationLastMessage(conversation.id, message);

      // 6. Increment unread count for other participants
      await ConversationService.incrementUnreadCount(conversation.id, senderId);

      // 7. Send notifications
      await this.sendMessageNotifications(message, conversation);

      // 8. Send mention notifications
      if (message.mentions && message.mentions.length > 0) {
        // Get sender's display name from user document
        const senderDoc = await getDoc(doc(db, 'users', senderId));
        const senderName = senderDoc.exists()
          ? (senderDoc.data().displayName || senderDoc.data().email)
          : 'Someone';

        await mentionService.processMentions(message.mentions, {
          contentType: 'message',
          contentId: messageId,
          authorId: senderId,
          authorName: senderName,
          contentPreview: message.content.substring(0, 100),
          conversationId: params.conversationId,
        });
      }

      // 9. Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'message' as any,
          entityId: messageId,
          changeType: 'create',
          beforeState: null,
          afterState: message,
          changedFields: Object.keys(message),
        },
        senderId,
        'User',
        false
      );

      return messageId;
    } catch (error: any) {
      console.error('Error sending message:', error);
      throw new Error(`Failed to send message: ${error.message}`);
    }
  }

  /**
   * Get message by ID
   */
  async getMessageById(messageId: string): Promise<Message | null> {
    try {
      const docRef = doc(db, this.messagesCollection, messageId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.fromFirestore(docSnap.id, docSnap.data());
    } catch (error: any) {
      console.error('Error getting message:', error);
      throw new Error(`Failed to get message: ${error.message}`);
    }
  }

  /**
   * Get messages for a conversation (paginated)
   */
  async getConversationMessages(
    conversationId: string,
    limitCount: number = 50,
    beforeMessageId?: string
  ): Promise<Message[]> {
    try {
      const q = query(
        collection(db, this.messagesCollection),
        where('conversationId', '==', conversationId),
        where('deleted', '==', false),
        orderBy('createdAt', 'desc'),
        firestoreLimit(limitCount)
      );

      // TODO: Implement cursor-based pagination with beforeMessageId

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.fromFirestore(doc.id, doc.data()));
    } catch (error: any) {
      console.error('Error getting conversation messages:', error);
      throw new Error(`Failed to get messages: ${error.message}`);
    }
  }

  /**
   * Edit a message (within 24 hours)
   */
  async editMessage(
    messageId: string,
    content: string,
    userId: string
  ): Promise<void> {
    try {
      // 1. Get message
      const message = await this.getMessageById(messageId);
      if (!message) {
        throw new MessageValidationError('Message not found');
      }

      // 2. Check permissions
      if (message.senderId !== userId) {
        throw new MessageValidationError('Only the sender can edit this message');
      }
      if (message.deleted) {
        throw new MessageValidationError('Cannot edit deleted message');
      }

      // 3. Check 24-hour window
      const hoursSinceSent = (Date.now() - message.createdAt.getTime()) / (1000 * 60 * 60);
      if (hoursSinceSent > 24) {
        throw new MessageValidationError('Can only edit messages within 24 hours');
      }

      // 4. Update Firestore
      const docRef = doc(db, this.messagesCollection, messageId);
      await updateDoc(docRef, {
        content,
        edited: true,
        editedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      // 5. Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'message' as any,
          entityId: messageId,
          changeType: 'update',
          beforeState: message,
          afterState: { ...message, content, edited: true },
          changedFields: ['content', 'edited', 'editedAt'],
        },
        userId,
        'User',
        false
      );
    } catch (error: any) {
      console.error('Error editing message:', error);
      throw error;
    }
  }

  /**
   * Delete a message (soft delete)
   */
  async deleteMessage(messageId: string, userId: string): Promise<void> {
    try {
      // 1. Get message
      const message = await this.getMessageById(messageId);
      if (!message) {
        throw new MessageValidationError('Message not found');
      }

      // 2. Check permissions
      if (message.senderId !== userId) {
        throw new MessageValidationError('Only the sender can delete this message');
      }

      // 3. Soft delete
      const docRef = doc(db, this.messagesCollection, messageId);
      await updateDoc(docRef, {
        deleted: true,
        deletedAt: Timestamp.now(),
        deletedBy: userId,
        content: '[Message deleted]', // Replace content
      });

      // 4. Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'message' as any,
          entityId: messageId,
          changeType: 'delete',
          beforeState: message,
          changedFields: ['deleted', 'deletedAt', 'deletedBy', 'content'],
        },
        userId,
        'User',
        false
      );
    } catch (error: any) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  /**
   * Add reaction to a message
   */
  async addReaction(
    messageId: string,
    emoji: string,
    userId: string
  ): Promise<void> {
    try {
      const message = await this.getMessageById(messageId);
      if (!message || message.deleted) {
        throw new MessageValidationError('Message not found or deleted');
      }

      const reactions = message.reactions || {};
      const userIds = reactions[emoji] || [];

      if (userIds.includes(userId)) {
        // Already reacted, do nothing
        return;
      }

      userIds.push(userId);
      reactions[emoji] = userIds;

      const docRef = doc(db, this.messagesCollection, messageId);
      await updateDoc(docRef, {
        [`reactions.${emoji}`]: userIds,
      });

      // Notify message sender if someone else reacted
      if (message.senderId !== userId) {
        await NotificationService.createNotification({
          userId: message.senderId,
          type: 'message_reaction',
          title: 'New reaction',
          message: `Someone reacted ${emoji} to your message`,
          actionUrl: `/messages/${message.conversationId}`,
          data: { messageId, emoji, reactedBy: userId },
        });
      }
    } catch (error: any) {
      console.error('Error adding reaction:', error);
      throw new Error(`Failed to add reaction: ${error.message}`);
    }
  }

  /**
   * Remove reaction from a message
   */
  async removeReaction(
    messageId: string,
    emoji: string,
    userId: string
  ): Promise<void> {
    try {
      const message = await this.getMessageById(messageId);
      if (!message) return;

      const reactions = message.reactions || {};
      const userIds = reactions[emoji] || [];

      const filtered = userIds.filter(id => id !== userId);
      if (filtered.length === 0) {
        // Remove emoji key if no reactions left
        delete reactions[emoji];
      } else {
        reactions[emoji] = filtered;
      }

      const docRef = doc(db, this.messagesCollection, messageId);
      await updateDoc(docRef, { reactions });
    } catch (error: any) {
      console.error('Error removing reaction:', error);
      throw new Error(`Failed to remove reaction: ${error.message}`);
    }
  }

  /**
   * Mark message as read by user
   */
  async markAsRead(messageId: string, userId: string): Promise<void> {
    try {
      const message = await this.getMessageById(messageId);
      if (!message || message.readBy.includes(userId)) {
        return; // Already read
      }

      const docRef = doc(db, this.messagesCollection, messageId);
      await updateDoc(docRef, {
        readBy: [...message.readBy, userId],
      });
    } catch (error: any) {
      console.error('Error marking as read:', error);
    }
  }

  /**
   * Search messages
   */
  async searchMessages(params: MessageSearchParams): Promise<Message[]> {
    try {
      let q = query(collection(db, this.messagesCollection));

      if (params.conversationId) {
        q = query(q, where('conversationId', '==', params.conversationId));
      }
      if (params.senderId) {
        q = query(q, where('senderId', '==', params.senderId));
      }
      if (params.types && params.types.length > 0) {
        q = query(q, where('type', 'in', params.types));
      }

      q = query(q, where('deleted', '==', false));
      q = query(q, orderBy('createdAt', 'desc'));

      if (params.limit) {
        q = query(q, firestoreLimit(params.limit));
      }

      const snapshot = await getDocs(q);
      let messages = snapshot.docs.map(doc => this.fromFirestore(doc.id, doc.data()));

      // Client-side filtering for search term (Firestore doesn't support full-text search)
      if (params.searchTerm) {
        const term = params.searchTerm.toLowerCase();
        messages = messages.filter(msg =>
          msg.content.toLowerCase().includes(term)
        );
      }

      // Client-side date filtering
      if (params.startDate) {
        messages = messages.filter(msg => msg.createdAt >= params.startDate!);
      }
      if (params.endDate) {
        messages = messages.filter(msg => msg.createdAt <= params.endDate!);
      }

      return messages;
    } catch (error: any) {
      console.error('Error searching messages:', error);
      throw new Error(`Failed to search messages: ${error.message}`);
    }
  }

  // ============ PRIVATE HELPER METHODS ============

  private validateCreateMessage(params: CreateMessageParams): void {
    if (!params.conversationId) {
      throw new MessageValidationError('conversationId is required');
    }
    if (!params.content || params.content.trim().length === 0) {
      throw new MessageValidationError('Message content cannot be empty');
    }
    if (params.content.length > 5000) {
      throw new MessageValidationError('Message content exceeds 5000 characters');
    }
    if (params.attachments && params.attachments.length > 10) {
      throw new MessageValidationError('Maximum 10 attachments per message');
    }
  }

  private async updateConversationLastMessage(
    conversationId: string,
    message: Message
  ): Promise<void> {
    try {
      const preview = {
        text: message.content.substring(0, 100),
        senderId: message.senderId,
        timestamp: message.createdAt,
        type: message.type,
      };

      const docRef = doc(db, 'conversations', conversationId);
      await updateDoc(docRef, {
        lastMessage: {
          ...preview,
          timestamp: Timestamp.fromDate(preview.timestamp),
        },
        lastActivity: Timestamp.fromDate(message.createdAt),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating conversation lastMessage:', error);
    }
  }

  private async sendMessageNotifications(
    message: Message,
    conversation: any
  ): Promise<void> {
    try {
      const otherParticipants = conversation.participants.filter(
        (id: string) => id !== message.senderId
      );

      for (const participantId of otherParticipants) {
        const memberInfo = conversation.memberInfo[participantId];

        // Check if user has muted this conversation
        if (memberInfo?.muted) continue;

        // Check notification preferences
        const notifyLevel = memberInfo?.notifications || 'all';
        let shouldNotify = false;

        if (conversation.type === 'direct') {
          shouldNotify = notifyLevel === 'all';
        } else {
          // Group chat
          if (notifyLevel === 'all') {
            shouldNotify = true;
          } else if (notifyLevel === 'mentions' && message.mentions?.includes(participantId)) {
            shouldNotify = true;
          }
        }

        if (shouldNotify) {
          const title = conversation.type === 'direct'
            ? 'New message'
            : `New message in ${conversation.name}`;

          const isMentioned = message.mentions?.includes(participantId);
          const messagePreview = isMentioned
            ? `@you: ${message.content.substring(0, 50)}`
            : message.content.substring(0, 50);

          await NotificationService.createNotification({
            userId: participantId,
            type: isMentioned ? 'message_mention' : 'new_message',
            title,
            message: messagePreview,
            actionUrl: `/messages/${conversation.id}`,
            actionLabel: 'View Message',
            data: {
              conversationId: conversation.id,
              messageId: message.id,
              senderId: message.senderId,
            },
          });
        }
      }
    } catch (error) {
      console.error('Error sending message notifications:', error);
      // Don't throw - notifications are non-critical
    }
  }

  /**
   * Convert Firestore timestamps to Date objects
   */
  private fromFirestore(id: string, data: any): Message {
    return {
      ...data,
      id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      editedAt: data.editedAt?.toDate(),
      deletedAt: data.deletedAt?.toDate(),
    } as Message;
  }

  /**
   * Convert Date objects to Firestore timestamps
   * Also removes undefined fields to prevent Firestore errors
   */
  private toFirestore(message: Message): any {
    const data: any = {
      id: message.id,
      conversationId: message.conversationId,
      senderId: message.senderId,
      type: message.type,
      content: message.content,
      reactions: message.reactions || {},
      readBy: message.readBy,
      edited: message.edited,
      deleted: message.deleted,
      createdAt: Timestamp.fromDate(message.createdAt),
    };

    // Only include optional fields if they exist
    if (message.attachments && message.attachments.length > 0) {
      data.attachments = message.attachments;
    }
    if (message.replyTo) {
      data.replyTo = message.replyTo;
    }
    if (message.mentions && message.mentions.length > 0) {
      data.mentions = message.mentions;
    }
    if (message.updatedAt) {
      data.updatedAt = Timestamp.fromDate(message.updatedAt);
    }
    if (message.editedAt) {
      data.editedAt = Timestamp.fromDate(message.editedAt);
    }
    if (message.deletedAt) {
      data.deletedAt = Timestamp.fromDate(message.deletedAt);
    }
    if (message.deletedBy) {
      data.deletedBy = message.deletedBy;
    }

    return data;
  }
}

export const MessageService = new MessageServiceImpl();
